import { Image } from '#components/Image/Image';
import { ImageModule } from '#components/Image/ImageModule';
import { MediaAudio } from '#components/Media/MediaAudio';
import { MediaLivestream } from '#components/Media/MediaLivestream';
import { MediaVideo } from '#components/Media/MediaVideo';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import type { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { getDurationFromMediaModule } from '#cutils/media/getDurationFromMediaModule';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import { lg, xl } from '#shared/styles/breakpoints';
import { logger } from '#utils/logging/logger';

import styles from '../../../../shared/styles/mediacontainer.module.scss';

import type { ArticleHeroMediaFragment } from './__generated__/ArticleHeroMedia.generated';

type Props = {
	articlePublicationDate?: string | null;
	teaserModule: ArticleHeroMediaFragment;
	isPriority?: boolean;
	onPlayStatusChange(status: MediaContextPlaybackStatus): void;
};

export const HERO_IMAGE_SIZES = `(max-width: ${lg}px) 100vw, (max-width: ${xl}px) calc(100vw - 16.25rem), calc(100vw - 21rem)`;

export function ArticleHeroMedia({ articlePublicationDate, teaserModule, isPriority, onPlayStatusChange }: Props) {
	const { images } = useEnvironment();

	if (!teaserModule) {
		return null;
	}

	const thumbnail = teaserModule.thumbnail;

	if (!thumbnail) {
		logger.error(`Thumbnail for hero media not found for teaser module with id '%s'.`, teaserModule.rowId);

		const teaserImage = images.FALLBACK_IMAGE;
		return <Image src={teaserImage} alt="BR24" isPriority={isPriority} sizes={HERO_IMAGE_SIZES} />;
	}

	switch (teaserModule.type) {
		case 'IMAGE':
			return <ImageModule image={thumbnail} isPriority={isPriority} sizes={HERO_IMAGE_SIZES} />;

		case 'AUDIO':
			if (!teaserModule.audio || !teaserModule.audio.url) {
				logger.error(`Module in hero is of type 'audio', but no audio or url was found '%s'.`, teaserModule.rowId);

				return <ImageModule image={thumbnail} isPriority={isPriority} sizes={HERO_IMAGE_SIZES} />;
			}

			const audioDuration = getDurationFromMediaModule(teaserModule);

			return (
				<div>
					<MediaAudio
						copyright={teaserModule.copyright}
						duration={audioDuration}
						meta={teaserModule.meta}
						publicationDate={articlePublicationDate}
						thumbnailAltText={thumbnail.altText}
						thumbnailCopyright={thumbnail.copyright}
						thumbnailUrl={thumbnail.url}
						title={teaserModule.title || undefined}
						url={teaserModule.audio.url}
						isPriority={isPriority}
						onPlayStatusChange={onPlayStatusChange}
						isHero={true}
						rowId={teaserModule.rowId}
						sizes={HERO_IMAGE_SIZES}
					/>
				</div>
			);

		case 'LIVESTREAM':
			const livestreamStartDate = teaserModule?.livestream?.start ?? null;
			const livestreamEndDate = teaserModule?.livestream?.end ?? null;
			const livestreamHlsUrl = teaserModule?.livestream?.hls ?? null;
			const onDemandVideos = (teaserModule.livestream?.programme?.videoFiles?.nodes ?? [])
				.filter(notNullOrUndefined)
				.map(({ publicLocation }) => publicLocation)
				.filter(notNullOrUndefined);

			if (!livestreamHlsUrl) {
				logger.error(`Module in hero is of type 'livestream', but no url was found '%s'.`, teaserModule.rowId);

				return <ImageModule image={thumbnail} isPriority={isPriority} sizes={HERO_IMAGE_SIZES} />;
			}

			return (
				<div>
					<MediaLivestream
						copyright={teaserModule.copyright}
						livestreamStartDate={livestreamStartDate}
						livestreamEndDate={livestreamEndDate}
						liveStreamHlsUrl={livestreamHlsUrl}
						thumbnailAltText={thumbnail.altText}
						thumbnailCopyright={thumbnail.copyright}
						thumbnailUrl={thumbnail.url}
						title={teaserModule.title || undefined}
						isPriority={isPriority}
						isHero={true}
						moduleId={teaserModule.rowId}
						videos={onDemandVideos}
						publicationDate={articlePublicationDate}
						onPlayStatusChange={onPlayStatusChange}
						sizes={HERO_IMAGE_SIZES}
					/>
				</div>
			);

		case 'VIDEO':
			if (!teaserModule.video?.hls) {
				logger.error(`Module in hero is of type 'video', but no video or url was found '%s'.`, teaserModule.rowId);

				return <ImageModule image={thumbnail} isPriority={isPriority} sizes={HERO_IMAGE_SIZES} />;
			}

			const videoDuration = getDurationFromMediaModule(teaserModule);

			return (
				<div className={`${styles.mediaRoot}`}>
					<MediaVideo
						copyright={teaserModule.copyright}
						duration={videoDuration}
						meta={teaserModule.meta}
						publicationDate={articlePublicationDate}
						thumbnailAltText={thumbnail.altText}
						thumbnailCopyright={thumbnail.copyright}
						thumbnailUrl={thumbnail.url}
						title={teaserModule.title || undefined}
						url={teaserModule.video.hls}
						isPriority={isPriority}
						onPlayStatusChange={onPlayStatusChange}
						isHero={true}
						moduleId={teaserModule.rowId}
						sizes={HERO_IMAGE_SIZES}
					/>
				</div>
			);

		default:
			return <ImageModule image={thumbnail} sizes={HERO_IMAGE_SIZES} />;
	}
}
